/**
 * AdSetRow component for displaying ad sets with expandable ads
 */

import { ChevronRight, ChevronDown, Loader2, Layers } from 'lucide-react';
import { useState } from 'react';

import { useAds } from '@/hooks/useMetaBrowse';
import { cn } from '@/lib/utils';
import type { MetaAdSet } from '@/types/meta';

import { AdRow } from './AdRow';

interface AdSetRowProps {
  adSet: MetaAdSet;
  teamId: string;
  selectedAds: Set<string>;
  onSelectAd: (adId: string, selected: boolean) => void;
}

export function AdSetRow({
  adSet,
  teamId,
  selectedAds,
  onSelectAd,
}: AdSetRowProps) {
  const [expanded, setExpanded] = useState(false);

  // Only fetch ads when expanded
  const { data: ads, isLoading } = useAds({
    teamId,
    adSetId: expanded ? adSet.id : null,
    enabled: expanded,
  });

  // Format budget for display
  const formatBudget = (amount: number | null): string => {
    if (amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  return (
    <div>
      {/* Ad Set Header */}
      <button
        onClick={handleToggle}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-3 text-left',
          'hover:bg-surface-subtle transition-colors',
          'border-b border-border-subtle',
          'ml-6' // Indent under campaign
        )}
      >
        {/* Expand/Collapse */}
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-muted flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted flex-shrink-0" />
        )}

        {/* Icon */}
        <Layers className="h-4 w-4 text-muted flex-shrink-0" />

        {/* Ad Set Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-primary truncate">
              {adSet.name}
            </span>
            <span className={cn(
              'text-xs',
              adSet.status === 'ACTIVE' ? 'text-success' : 'text-muted'
            )}>
              {adSet.status}
            </span>
          </div>
        </div>

        {/* Metrics */}
        <div className="flex items-center gap-4 text-xs text-muted">
          {adSet.dailyBudget && (
            <span>{formatBudget(adSet.dailyBudget)}/day</span>
          )}
          {adSet.lifetimeBudget && (
            <span>{formatBudget(adSet.lifetimeBudget)} lifetime</span>
          )}
          <span className="font-medium text-secondary">
            {adSet.videoAdCount} video{adSet.videoAdCount !== 1 ? 's' : ''}
          </span>
        </div>
      </button>

      {/* Expanded Ads */}
      {expanded && (
        <div className="bg-surface-subtle/50">
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 text-muted animate-spin" />
              <span className="ml-2 text-sm text-muted">Loading ads...</span>
            </div>
          ) : ads && ads.length > 0 ? (
            ads.map((ad) => (
              <AdRow
                key={ad.id}
                ad={ad}
                selected={selectedAds.has(ad.id)}
                onSelect={onSelectAd}
              />
            ))
          ) : (
            <div className="py-4 text-center text-sm text-muted">
              No ads in this ad set
            </div>
          )}
        </div>
      )}
    </div>
  );
}
